import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

const Pagination = ({
  currentPage,
  current,
  totalPages,
  total,
  totalItems,
  pageSize = 10,
  onPageChange,
  onChange,
  showInfo = true,
  showQuickNav = true,
  maxVisiblePages = 5,
  className = '',
}) => {
  const resolvedCurrentPage = Number(currentPage ?? current ?? 1)
  const resolvedTotalPages = Number(totalPages ?? total ?? 1)
  const handleChange = onPageChange ?? onChange

  const getPageNumbers = () => {
    const pages = []
    let startPage = Math.max(1, resolvedCurrentPage - Math.floor(maxVisiblePages / 2))
    let endPage = Math.min(resolvedTotalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    if (startPage > 1) {
      pages.push(1)
      if (startPage > 2) {
        pages.push('...')
      }
    }

    for (let i = startPage; i <= endPage; i += 1) {
      pages.push(i)
    }

    if (endPage < resolvedTotalPages) {
      if (endPage < resolvedTotalPages - 1) {
        pages.push('...')
      }
      pages.push(resolvedTotalPages)
    }

    return pages
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= resolvedTotalPages && page !== resolvedCurrentPage) {
      handleChange?.(page)
    }
  }

  const startItem = totalItems ? ((resolvedCurrentPage - 1) * pageSize + 1) : 0
  const endItem = Math.min(resolvedCurrentPage * pageSize, totalItems || 0)

  if (resolvedTotalPages <= 1) return null

  return (
    <div className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${className}`.trim()}>
      {showInfo && totalItems != null && (
        <div className="text-sm text-gray-500">
          Mostrando <span className="font-medium">{startItem}</span> a{' '}
          <span className="font-medium">{endItem}</span> de{' '}
          <span className="font-medium">{totalItems}</span> resultados
        </div>
      )}

      <div className="flex items-center space-x-1">
        {showQuickNav && (
          <button
            onClick={() => handlePageChange(1)}
            disabled={resolvedCurrentPage === 1}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Primera pagina"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
        )}

        <button
          onClick={() => handlePageChange(resolvedCurrentPage - 1)}
          disabled={resolvedCurrentPage === 1}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Pagina anterior"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="flex items-center space-x-1">
          {getPageNumbers().map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`
                  min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium transition-colors
                  ${page === resolvedCurrentPage
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                  }
                `.trim().replace(/\s+/g, ' ')}
                aria-current={page === resolvedCurrentPage ? 'page' : undefined}
              >
                {page}
              </button>
            )
          ))}
        </div>

        <button
          onClick={() => handlePageChange(resolvedCurrentPage + 1)}
          disabled={resolvedCurrentPage === resolvedTotalPages}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Pagina siguiente"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        {showQuickNav && (
          <button
            onClick={() => handlePageChange(resolvedTotalPages)}
            disabled={resolvedCurrentPage === resolvedTotalPages}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Ultima pagina"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}

export const SimplePagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  className = '',
}) => {
  return (
    <div className={`flex items-center space-x-2 ${className}`.trim()}>
      <button
        onClick={() => onPageChange?.(currentPage - 1)}
        disabled={currentPage === 1}
        className="btn btn-secondary btn-sm"
      >
        Anterior
      </button>
      <span className="text-sm text-gray-500">
        Pagina {currentPage} de {totalPages}
      </span>
      <button
        onClick={() => onPageChange?.(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="btn btn-secondary btn-sm"
      >
        Siguiente
      </button>
    </div>
  )
}

export default Pagination
